import { alpha } from '@mui/material/styles';
import { THEME_COLORS } from '../shared/constants/theme';
import { DASHBOARD_ACCENT } from './dashboardSurfaceSx';
import { ULTRA_GRID_ACCENT } from './dashboardUltraGridLayout';
import { ROOM_BOARD_ACCENT } from './roomBoardStatusColors';

export const ROOM_PICKER_ACCENT = ULTRA_GRID_ACCENT;

const BODY_BG_DARK = '#141820';

export function getRoomPickerPageTitleSx(isDark) {
  return {
    color: isDark ? '#f8fafc' : '#0f172a',
    fontWeight: 800,
    fontSize: { xs: '1.05rem', sm: '1.2rem' },
    letterSpacing: '-0.03em',
    lineHeight: 1.15,
  };
}

export function getRoomPickerTypeSectionSx(isDark, { allowOverflow = false } = {}) {
  return {
    mb: 2.5,
    borderRadius: '16px',
    overflow: allowOverflow ? 'visible' : 'hidden',
    border: `1px solid ${isDark ? alpha('#ffffff', 0.08) : alpha('#000', 0.08)}`,
    background: isDark
      ? `linear-gradient(165deg, ${alpha('#ffffff', 0.03)} 0%, ${BODY_BG_DARK} 100%)`
      : 'linear-gradient(165deg, #ffffff 0%, #f8fafc 100%)',
    boxShadow: isDark
      ? '0 12px 28px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.04)'
      : '0 8px 22px rgba(15,23,42,0.06)',
  };
}

export function getRoomPickerTypeHeaderSx(isDark, accent = ROOM_PICKER_ACCENT) {
  return {
    px: { xs: 1.1, sm: 1.35 },
    py: { xs: 1, sm: 1.15 },
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 1,
    borderBottom: `1px solid ${isDark ? alpha('#ffffff', 0.07) : alpha('#000', 0.06)}`,
    background: isDark ? THEME_COLORS.SURFACE_HEADER_DARK : alpha(accent, 0.03),
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 3,
      background: `linear-gradient(90deg, ${alpha(accent, 0.15)} 0%, ${accent} 50%, ${alpha(accent, 0.15)} 100%)`,
    },
  };
}

export function getRoomPickerTypeNameSx(isDark) {
  return {
    color: isDark ? '#f1f5f9' : '#0f172a',
    fontWeight: 800,
    fontSize: { xs: '0.82rem', sm: '0.92rem' },
    letterSpacing: '-0.02em',
    lineHeight: 1.25,
  };
}

export function getRoomPickerAvailabilityHintSx(isDark, { disabled = false } = {}) {
  const accent = disabled ? '#94a3b8' : ROOM_PICKER_ACCENT;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.65,
    maxWidth: '100%',
    px: 1,
    py: 0.55,
    borderRadius: '12px',
    bgcolor: isDark ? alpha(accent, disabled ? 0.08 : 0.1) : alpha(accent, disabled ? 0.06 : 0.08),
    border: `1px solid ${alpha(accent, isDark ? 0.28 : 0.22)}`,
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
  };
}

export function getRoomPickerAvailabilityTextSx(isDark, { disabled = false } = {}) {
  return {
    color: disabled
      ? (isDark ? alpha('#cbd5e1', 0.72) : '#64748b')
      : (isDark ? alpha('#ecfdf5', 0.92) : '#065f46'),
    fontSize: '0.72rem',
    fontWeight: 600,
    lineHeight: 1.35,
    letterSpacing: '0.01em',
  };
}

export function getRoomPickerStatChipSx(isDark, kind) {
  const tokens = {
    available: {
      color: isDark ? '#cbd5e1' : '#475569',
      bg: isDark ? alpha('#ffffff', 0.06) : alpha('#000', 0.04),
      border: isDark ? alpha('#ffffff', 0.14) : alpha('#000', 0.1),
    },
    booked: {
      color: isDark ? '#ffcc80' : '#c2410c',
      bg: isDark ? alpha(ROOM_BOARD_ACCENT.booked, 0.14) : alpha(ROOM_BOARD_ACCENT.booked, 0.1),
      border: alpha(ROOM_BOARD_ACCENT.booked, isDark ? 0.35 : 0.28),
    },
    blocked: {
      color: isDark ? '#fca5a5' : '#b91c1c',
      bg: isDark ? alpha(ROOM_BOARD_ACCENT.blocked, 0.14) : alpha(ROOM_BOARD_ACCENT.blocked, 0.08),
      border: alpha(ROOM_BOARD_ACCENT.blocked, isDark ? 0.35 : 0.28),
    },
  };
  const t = tokens[kind] || tokens.available;
  return {
    height: 24,
    fontSize: '0.62rem',
    fontWeight: 800,
    letterSpacing: '0.02em',
    borderRadius: '999px',
    bgcolor: t.bg,
    color: t.color,
    border: `1px solid ${t.border}`,
    boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.05)' : '0 1px 2px rgba(15,23,42,0.05)',
    '& .MuiChip-label': { px: 0.85 },
  };
}

export function getRoomPickerGridWrapSx() {
  return {
    p: { xs: 1, sm: 1.25 },
  };
}

export function getRoomPickerCardSx(isDark, isMobile, options = {}) {
  const {
    isSelected = false,
    isDisabled = false,
    accent = ROOM_PICKER_ACCENT,
  } = options;

  const dot = isSelected ? accent : (isDark ? '#64748b' : '#94a3b8');

  return {
    borderRadius: isMobile ? '14px' : '16px',
    height: '100%',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    opacity: isDisabled ? 0.62 : 1,
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    background: isDark
      ? `linear-gradient(165deg, ${alpha('#ffffff', isSelected ? 0.06 : 0.04)} 0%, ${BODY_BG_DARK} 100%)`
      : (isSelected
        ? 'linear-gradient(165deg, #ecfdf5 0%, #ffffff 100%)'
        : 'linear-gradient(165deg, #ffffff 0%, #f8fafc 100%)'),
    border: isSelected
      ? `1px solid ${alpha(accent, isDark ? 0.5 : 0.42)}`
      : `1px solid ${isDark ? alpha('#ffffff', 0.1) : alpha('#000', 0.08)}`,
    boxShadow: isSelected
      ? (isDark
        ? `0 14px 28px rgba(0,0,0,0.38), inset 0 0 0 1px ${alpha(accent, 0.22)}`
        : `0 10px 24px rgba(15,23,42,0.1), inset 0 0 0 1px ${alpha(accent, 0.18)}`)
      : (isDark
        ? '0 8px 20px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.04)'
        : '0 4px 14px rgba(15,23,42,0.06)'),
    transition: 'transform 0.22s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.22s ease, border-color 0.22s ease',
    WebkitTapHighlightColor: 'transparent',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 3,
      zIndex: 1,
      background: isSelected
        ? `linear-gradient(90deg, ${alpha(accent, 0.2)} 0%, ${accent} 50%, ${alpha(accent, 0.2)} 100%)`
        : `linear-gradient(90deg, ${alpha(dot, 0.12)} 0%, ${alpha(dot, 0.35)} 50%, ${alpha(dot, 0.12)} 100%)`,
    },
    ...(!isDisabled
      ? {
          '&:hover': {
            transform: isMobile ? 'none' : 'translateY(-3px)',
            borderColor: alpha(accent, isDark ? 0.45 : 0.38),
            boxShadow: isDark
              ? `0 16px 32px rgba(0,0,0,0.42), 0 0 0 1px ${alpha(accent, 0.2)}`
              : '0 12px 26px rgba(15,23,42,0.12)',
          },
          '&:active': {
            transform: isMobile ? 'scale(0.985)' : 'translateY(-1px)',
          },
        }
      : {}),
  };
}

export function getRoomPickerCardContentSx(isMobile) {
  return {
    p: isMobile ? 1.15 : 1.5,
    pt: isMobile ? 1.25 : 1.6,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: isMobile ? 0.85 : 1,
    position: 'relative',
    '&:last-child': { pb: isMobile ? 1.15 : 1.5 },
  };
}

export function getRoomPickerRoomImageSx(isMobile, isDark, { isSelected = false, accent = ROOM_PICKER_ACCENT } = {}) {
  const size = isMobile ? 52 : 60;
  return {
    width: size,
    height: size,
    flexShrink: 0,
    bgcolor: isDark ? alpha('#ffffff', 0.06) : alpha('#000', 0.04),
    color: isDark ? alpha(accent, 0.85) : accent,
    border: `2px solid ${isDark ? alpha('#ffffff', 0.12) : alpha('#000', 0.08)}`,
    boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.05)' : '0 4px 12px rgba(15,23,42,0.08)',
    transition: 'transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease',
    ...(isSelected
      ? {
          borderColor: alpha(accent, isDark ? 0.55 : 0.45),
          boxShadow: `0 0 0 3px ${alpha(accent, 0.18)}`,
        }
      : {}),
  };
}

export function getRoomPickerRoomImageWrapSx(isMobile) {
  const size = isMobile ? 52 : 60;
  return {
    position: 'relative',
    flexShrink: 0,
    width: size,
    height: size,
  };
}

export function getRoomPickerAvatarSize(isMobile) {
  return isMobile ? 52 : 60;
}

export function getRoomPickerStatusRowSx(isMobile) {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
    flexWrap: 'wrap',
    mt: 'auto',
    pt: isMobile ? 0.15 : 0.2,
  };
}

export function getRoomPickerSelectedImageOverlaySx(isMobile, isDark, accent = ROOM_PICKER_ACCENT) {
  return {
    position: 'absolute',
    inset: 0,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    bgcolor: alpha(isDark ? '#020617' : '#0f172a', isDark ? 0.42 : 0.28),
    border: `2px solid ${alpha(accent, 0.85)}`,
    boxShadow: `
      0 0 0 3px ${alpha(accent, 0.22)},
      0 8px 20px ${alpha(accent, 0.35)},
      inset 0 0 16px ${alpha(accent, 0.12)}
    `,
    backdropFilter: 'blur(1px)',
    WebkitBackdropFilter: 'blur(1px)',
    animation: 'roomPickerCheckPop 0.28s cubic-bezier(0.22, 1, 0.36, 1)',
    '@keyframes roomPickerCheckPop': {
      '0%': { opacity: 0, transform: 'scale(0.82)' },
      '100%': { opacity: 1, transform: 'scale(1)' },
    },
  };
}

export function getRoomPickerSelectedImageCheckSx(isMobile, accent = ROOM_PICKER_ACCENT) {
  return {
    fontSize: isMobile ? 28 : 32,
    color: accent,
    filter: `drop-shadow(0 2px 8px ${alpha(accent, 0.65)})`,
  };
}

export function getRoomPickerSelectedBadgeSx(isMobile, accent = ROOM_PICKER_ACCENT, options = {}) {
  const { placement = 'right' } = options;
  return {
    position: 'absolute',
    top: isMobile ? 8 : 10,
    ...(placement === 'left' ? { left: isMobile ? 8 : 10 } : { right: isMobile ? 8 : 10 }),
    zIndex: 2,
    width: isMobile ? 26 : 30,
    height: isMobile ? 26 : 30,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    bgcolor: accent,
    color: '#042f2e',
    boxShadow: `0 6px 16px ${alpha(accent, 0.45)}`,
    border: `2px solid ${alpha('#ffffff', 0.85)}`,
  };
}

export function getRoomPickerRoomNumberSx(isMobile, isDark, isSelected = false) {
  return {
    fontWeight: 900,
    fontSize: isMobile ? '1.35rem' : '1.55rem',
    lineHeight: 1,
    letterSpacing: '-0.04em',
    fontVariantNumeric: 'tabular-nums',
    color: isDark ? '#f8fafc' : '#0f172a',
    background: isSelected && !isDark
      ? `linear-gradient(135deg, #0f172a 0%, ${DASHBOARD_ACCENT} 100%)`
      : undefined,
    WebkitBackgroundClip: isSelected && !isDark ? 'text' : undefined,
    WebkitTextFillColor: isSelected && !isDark ? 'transparent' : undefined,
  };
}

export function getRoomPickerFloorSx(isMobile, isDark) {
  return {
    mt: 0.35,
    color: isDark ? alpha('#cbd5e1', 0.78) : '#64748b',
    fontSize: isMobile ? '0.64rem' : '0.72rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
  };
}

export function getRoomPickerCleanStatusChipSx(cleanStatus, isMobile, isDark) {
  const normalized = String(cleanStatus || 'clean').toLowerCase();
  const colorMap = {
    clean: '#4caf50',
    dirty: '#ff9800',
    inspected: '#2196f3',
  };
  const color = colorMap[normalized] || '#94a3b8';
  const labelMap = {
    clean: 'Clean',
    dirty: 'Dirty',
    inspected: 'Inspected',
  };

  return {
    chipSx: {
      height: isMobile ? 22 : 24,
      fontSize: isMobile ? '0.62rem' : '0.68rem',
      fontWeight: 800,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      bgcolor: isDark ? alpha(color, 0.14) : alpha(color, 0.1),
      color: isDark ? alpha(color, 0.95) : color,
      border: `1px solid ${alpha(color, isDark ? 0.38 : 0.3)}`,
      boxShadow: isDark ? `inset 0 1px 0 ${alpha('#ffffff', 0.05)}` : `0 2px 6px ${alpha(color, 0.12)}`,
      '& .MuiChip-label': {
        px: 0.85,
        py: 0,
        lineHeight: 1,
      },
    },
    label: labelMap[normalized] || normalized,
  };
}

export function getRoomPickerHeldChipSx(isDark, kind = 'pending_checkout') {
  const tokens = {
    web_checkin: {
      color: isDark ? '#5eead4' : '#0f766e',
      bg: isDark ? alpha('#14b8a6', 0.16) : alpha('#14b8a6', 0.12),
      border: alpha('#14b8a6', isDark ? 0.4 : 0.3),
    },
    overdue_checkout: {
      color: isDark ? '#fda4af' : '#be123c',
      bg: isDark ? alpha('#f43f5e', 0.16) : alpha('#f43f5e', 0.1),
      border: alpha('#f43f5e', isDark ? 0.4 : 0.28),
    },
    maintenance: {
      color: isDark ? '#cbd5e1' : '#475569',
      bg: isDark ? alpha('#94a3b8', 0.16) : alpha('#94a3b8', 0.12),
      border: alpha('#94a3b8', isDark ? 0.35 : 0.28),
    },
    pending_checkout: {
      color: isDark ? '#ffcc80' : '#c2410c',
      bg: isDark ? alpha('#ff9800', 0.16) : alpha('#ff9800', 0.1),
      border: alpha('#ff9800', isDark ? 0.38 : 0.28),
    },
  };
  const t = tokens[kind] || tokens.pending_checkout;
  return {
    height: 'auto',
    minHeight: 24,
    maxWidth: '100%',
    fontWeight: 800,
    fontSize: '0.72rem',
    letterSpacing: '0.01em',
    bgcolor: t.bg,
    color: t.color,
    border: `1px solid ${t.border}`,
    '& .MuiChip-label': {
      display: 'block',
      whiteSpace: 'normal',
      overflow: 'visible',
      textOverflow: 'clip',
      px: 0.9,
      py: 0.35,
      lineHeight: 1.25,
    },
  };
}
